import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import {
  MESSAGE_MAX_LENGTH,
  AUDIO_FORMATS,
} from 'shared/helpers/MessageTypeHelper';

// --- Configuration ---

export const INBOX_FEATURES = {
  REPLY_TO: 'replyTo',
  REPLY_TO_OUTGOING: 'replyToOutgoing',
  FILE_UPLOAD: 'fileUpload',
  MULTI_FILE_UPLOAD: 'multiFileUpload',
  RICH_EDITOR: 'richEditor',
  AUDIO_MP3: 'audioMp3',
};

const FEATURE_CONFIG = {
  [INBOX_FEATURES.REPLY_TO]: [
    INBOX_TYPES.FB,
    INBOX_TYPES.WEB,
    INBOX_TYPES.TWITTER,
    INBOX_TYPES.WHATSAPP,
    INBOX_TYPES.TELEGRAM,
    INBOX_TYPES.LINE,
    INBOX_TYPES.API,
  ],
  [INBOX_FEATURES.REPLY_TO_OUTGOING]: [
    INBOX_TYPES.WEB,
    INBOX_TYPES.TWITTER,
    INBOX_TYPES.WHATSAPP,
    INBOX_TYPES.TELEGRAM,
    INBOX_TYPES.API,
  ],
  [INBOX_FEATURES.FILE_UPLOAD]: [
    INBOX_TYPES.WEB,
    INBOX_TYPES.FB,
    INBOX_TYPES.WHATSAPP,
    INBOX_TYPES.API,
    INBOX_TYPES.EMAIL,
    INBOX_TYPES.SMS,
    INBOX_TYPES.TELEGRAM,
    INBOX_TYPES.LINE,
    INBOX_TYPES.INSTAGRAM,
  ],
  [INBOX_FEATURES.MULTI_FILE_UPLOAD]: [
    INBOX_TYPES.EMAIL,
    INBOX_TYPES.WEB,
    INBOX_TYPES.API,
    INBOX_TYPES.WHATSAPP,
    INBOX_TYPES.TELEGRAM,
  ],
  [INBOX_FEATURES.RICH_EDITOR]: [INBOX_TYPES.WEB, INBOX_TYPES.EMAIL],
  [INBOX_FEATURES.AUDIO_MP3]: [
    INBOX_TYPES.WHATSAPP,
    INBOX_TYPES.TELEGRAM,
    INBOX_TYPES.API,
  ],
};

const LENGTH_CONFIG = {
  [INBOX_TYPES.FB]: MESSAGE_MAX_LENGTH.FACEBOOK,
  [INBOX_TYPES.INSTAGRAM]: MESSAGE_MAX_LENGTH.INSTAGRAM,
  [INBOX_TYPES.SMS]: MESSAGE_MAX_LENGTH.TWILIO_SMS,
  [INBOX_TYPES.EMAIL]: MESSAGE_MAX_LENGTH.EMAIL,
  [INBOX_TYPES.WHATSAPP]: MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD,
  [INBOX_TYPES.TELEGRAM]: MESSAGE_MAX_LENGTH.TELEGRAM,
};

// --- Pure Domain Helpers ---

const hasFeature = (type, feature) =>
  (FEATURE_CONFIG[feature] || []).includes(type);

const isTwilioChannel = type => type === INBOX_TYPES.TWILIO;

const isTwilioWhatsApp = inbox => {
  if (!isTwilioChannel(inbox.channelType)) return false;
  return (
    inbox.medium === 'whatsapp' || inbox.phoneNumber?.startsWith('whatsapp')
  );
};

const isTwilioSMS = inbox => {
  if (!isTwilioChannel(inbox.channelType)) return false;
  return inbox.medium === 'sms';
};

const isWhatsAppCloud = inbox =>
  inbox.channelType === INBOX_TYPES.WHATSAPP &&
  inbox.provider === 'whatsapp_cloud';

const isAnyWhatsApp = inbox =>
  inbox.channelType === INBOX_TYPES.WHATSAPP || isTwilioWhatsApp(inbox);

const isAnySMS = inbox =>
  inbox.channelType === INBOX_TYPES.SMS || isTwilioSMS(inbox);

// --- Shared Resolution Logic ---

const resolveInbox = (source, getters) => {
  const { currentChat, inboxGetter } = getters;
  let target = source;

  if (!target && currentChat.value?.inbox_id) {
    target = currentChat.value.inbox_id;
  }

  if (['number', 'string'].includes(typeof target)) {
    const found = inboxGetter.value(target);
    return found ? useCamelCase(found, { deep: true }) : {};
  }

  return target ? useCamelCase(target, { deep: true }) : {};
};

const getInboxLength = inbox => {
  if (!inbox || !inbox.channelType) return MESSAGE_MAX_LENGTH.GENERAL;

  if (isTwilioWhatsApp(inbox)) return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
  if (isWhatsAppCloud(inbox)) return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
  if (isTwilioSMS(inbox)) return MESSAGE_MAX_LENGTH.TWILIO_SMS;

  return LENGTH_CONFIG[inbox.channelType] || MESSAGE_MAX_LENGTH.GENERAL;
};

// --- Exported Methods ---

/**
 * Теперь принимает source (ID или объект), резолвит его через стор и возвращает длину.
 * Внимание: Должна вызываться внутри setup() или injection context из-за useMapGetter.
 */
export const getInboxMaxLength = inboxSource => {
  const getters = {
    currentChat: useMapGetter('getSelectedChat'),
    inboxGetter: useMapGetter('inboxes/getInboxById'),
  };
  const inbox = resolveInbox(inboxSource, getters);
  return getInboxLength(inbox);
};

export const useInbox = (inboxSource = null) => {
  const getters = {
    currentChat: useMapGetter('getSelectedChat'),
    inboxGetter: useMapGetter('inboxes/getInboxById'),
  };

  const inbox = computed(() => resolveInbox(inboxSource, getters));

  const channelType = computed(() => inbox.value.channelType);
  // eslint-disable-next-line no-unused-vars
  const phoneNumber = computed(() => inbox.value.phoneNumber);
  const provider = computed(() => inbox.value.provider || '');

  // Computed checks
  const isATwilioChannel = computed(() => isTwilioChannel(channelType.value));
  const isATwilioWhatsAppChannel = computed(() =>
    isTwilioWhatsApp(inbox.value)
  );
  const isATwilioSMSChannel = computed(() => isTwilioSMS(inbox.value));
  const isAWhatsAppCloudChannel = computed(() => isWhatsAppCloud(inbox.value));
  const isAWhatsAppChannel = computed(() => isAnyWhatsApp(inbox.value));
  const isASmsInbox = computed(() => isAnySMS(inbox.value));
  const isAnEmailChannel = computed(
    () => channelType.value === INBOX_TYPES.EMAIL
  );

  const is360DialogWhatsAppChannel = computed(
    () =>
      channelType.value === INBOX_TYPES.WHATSAPP && provider.value === 'default'
  );
  const isAMicrosoftInbox = computed(
    () => isAnEmailChannel.value && provider.value === 'microsoft'
  );
  const isAGoogleInbox = computed(
    () => isAnEmailChannel.value && provider.value === 'google'
  );

  const isAPIInbox = computed(() => channelType.value === INBOX_TYPES.API);
  const isAFacebookInbox = computed(() => channelType.value === INBOX_TYPES.FB);
  const isATwitterInbox = computed(
    () => channelType.value === INBOX_TYPES.TWITTER
  );
  const isAWebWidgetInbox = computed(
    () => channelType.value === INBOX_TYPES.WEB
  );
  const isALineChannel = computed(() => channelType.value === INBOX_TYPES.LINE);
  const isATelegramChannel = computed(
    () => channelType.value === INBOX_TYPES.TELEGRAM
  );
  const isAnInstagramChannel = computed(
    () => channelType.value === INBOX_TYPES.INSTAGRAM
  );

  const inboxHasFeature = feature => hasFeature(channelType.value, feature);

  const showFileUpload = computed(
    () =>
      hasFeature(channelType.value, INBOX_FEATURES.FILE_UPLOAD) ||
      isASmsInbox.value
  );

  const enableMultipleFileUpload = computed(() =>
    hasFeature(channelType.value, INBOX_FEATURES.MULTI_FILE_UPLOAD)
  );

  const isRichEditorSupported = computed(() =>
    hasFeature(channelType.value, INBOX_FEATURES.RICH_EDITOR)
  );

  const audioRecordFormat = computed(() =>
    hasFeature(channelType.value, INBOX_FEATURES.AUDIO_MP3)
      ? AUDIO_FORMATS.MP3
      : AUDIO_FORMATS.WAV
  );

  const channelMaxLength = computed(() => getInboxLength(inbox.value));

  return {
    inbox,
    channelType,
    isAFacebookInbox,
    isALineChannel,
    isAPIInbox,
    isASmsInbox,
    isATelegramChannel,
    isATwilioChannel,
    isAWebWidgetInbox,
    isAWhatsAppChannel,
    isATwitterInbox,
    isAMicrosoftInbox,
    isAGoogleInbox,
    isATwilioWhatsAppChannel,
    isAWhatsAppCloudChannel,
    is360DialogWhatsAppChannel,
    isAnEmailChannel,
    isAnInstagramChannel,
    isATwilioSMSChannel,
    inboxHasFeature,
    INBOX_FEATURE_MAP: FEATURE_CONFIG,
    channelMaxLength,
    showFileUpload,
    enableMultipleFileUpload,
    audioRecordFormat,
    isRichEditorSupported,
  };
};
